// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.Izenda.ReportPartGridPivotActions.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React from 'react';
import { action } from 'mobx';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import { compose, withPropsOnChange, withProps, withHandlers } from 'recompose';
import ProcessData from 'logic/Izenda.ReportPartGridProcessData';
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import BrowserUtils from 'share/utils/Izenda.BrowserUtils';
import ColumnDataObject from 'logic/models/Izenda.ColumnDataObject';
import constants from 'logic/data/Izenda.ReportPartGridConstants';
import stylingUtil from './Izenda.ReportPartGridStylingUtil';
const COLLAPSE_STATUS = constants.COLLAPSE_STATUS;

import {
  head,
  defaults,
  get,
  getOr,
  map,
  isEmpty,
  isNil,
  mapValues,
  concat,
  flow,
  reduce,
  curry,
  some,
  set,
  chunk,
  every,
  sortBy,
  find,
  last,
  placeholder,
  toString
} from 'lodash/fp';

function getCellData(props: any, columnData: any, rowData: any) {
  const { reportPart, forDrillDown, subTotalMapping } = props;
  const rowExpanders = reportPart.reportPartContent.haveRowExpander;
  const element = columnData.element;

  const value = ProcessData.getDataForCell(columnData, rowData, rowExpanders, forDrillDown, subTotalMapping);

  const source = {
    element: ReportPartUtils.buildDataSourceElement(reportPart, element),
    record: element.properties.hasRangePercent ? head(ProcessData.getRawData(columnData, rowData)) : {}
  };

  return { value, source };
}

function getCellContentStyle(props, columnData, rowData, columnKey, rowIndex, column, columnIndex, cellData) {
  const { value, source } = cellData;
  const element = columnData.element;
  const cellStyle = element.properties.getUIStyle();
  const reportPartProperties = props.reportPart.getReportPartProperties();
  const columnAlignment = reportPartProperties.columns.alignment;
  const columnWordWrap = reportPartProperties.columns.wordWrap;

  // Only check wordWrap in Report Part Properties if in FieldProperties, wordWrap is null
  cellStyle.wordWrap = isNil(cellStyle.wordWrap) ? columnWordWrap : cellStyle.wordWrap;
  cellStyle.alignment = isNil(cellStyle.alignment) ? columnAlignment : cellStyle.alignment;

  const styleObject = stylingUtil.createStyleObject(cellStyle);

  if (!!value.isEmpty) {
    return styleObject;
  }

  const dataCellContent = !isNil(value.orgData) && value.orgData !== 'null' ? value.orgData : value.data;

  if (!rowData.currentRow.isSupTotalRow) {
    styleObject.color = ReportPartUtils.getConfigDataByRange(
      dataCellContent,
      element.properties.dataFormattings.color.textColor,
      source
    );
  }

  return styleObject;
}

function getCellStyle(props, columnData, columnLocInfo, numberOfRow, rowData, rowIndex, columnIndex, cellData) {
  const { value, source } = cellData;
  const reportPart = props.reportPart;
  const reportPartProperties = reportPart.getReportPartProperties();
  const columnVerticalAlignment = reportPartProperties && reportPartProperties.columns.verticalAlignment;
  let styleObject = {};
  const element = columnData.element;

  // Cell border style
  const borderStyle = stylingUtil.getCellStyleSetting(reportPart, columnLocInfo, rowIndex, columnIndex, numberOfRow);
  const currentRow = rowData.currentRow;

  if (
    !!value.isEmpty ||
    (currentRow.isSupTotalRow &&
      (!props.forDrillDown ||
        columnData.type !== ColumnDataObject.COLUMN_TYPE.ROW ||
        currentRow.rowDataObject.isExpand))
  ) {
    return borderStyle;
  }

  const dataCellContent = !isNil(value.orgData) && value.orgData !== 'null' ? value.orgData : value.data;

  if (!currentRow.isSupTotalRow) {
    styleObject = {
      backgroundColor: ReportPartUtils.getConfigDataByRange(
        dataCellContent,
        element.properties.dataFormattings.color.cellColor,
        source
      ),
      verticalAlign: element.properties.dataFormattings.verticalAlignment || columnVerticalAlignment
    };
  }

  return defaults(borderStyle, styleObject);
}

function fnGetHeaderCellStyle(props, columnData, columnLocInfo, numberOfRow, rowData, rowIndex, columnIndex) {
  const reportPart = props.reportPart;
  const properties = reportPart.getReportPartProperties();
  const reportPartHeaderProperties = properties.headers;

  const headerStyle = stylingUtil.getCellStyleSetting(
    reportPart,
    columnLocInfo,
    rowIndex,
    columnIndex,
    numberOfRow,
    true
  );

  const elementStyle = {
    backgroundColor: columnData.element.properties.getDefaultsHeaderProperty(
      'font.backgroundColor',
      reportPartHeaderProperties
    ),
    verticalAlign: columnData.element.properties.getDefaultsHeaderProperty(
      'verticalAlignment',
      reportPartHeaderProperties
    )
  };

  return defaults(headerStyle, elementStyle);
}

function fnGetHeaderCellContentStyle(props: any, columnData: any, e: any) {
  const reportPart = props.reportPart;
  const properties = reportPart.getReportPartProperties();
  const reportPartHeaderProperties = properties.headers;
  const cellStyle = columnData.element.properties.getHeaderCellStyle(reportPartHeaderProperties);

  return stylingUtil.createStyleObject(cellStyle);
}

function fnGetHeaderCellContentForPivotHeader(props: any, columnData: any, e: any) {
  const element = columnData.element;
  const reportPart = props.reportPart;

  if (isNil(columnData.element)) {
    if (columnData.isColumnGroup) {
      const properties = reportPart.getReportPartProperties();
      const reportPartHeaderProperties = properties.headers;

      return stylingUtil.createStyleObject({
        fontFamily: get('font.family', reportPartHeaderProperties),
        fontSize: get('font.size', reportPartHeaderProperties),
        bold: get('font.bold', reportPartHeaderProperties),
        italic: get('font.italic', reportPartHeaderProperties),
        underline: get('font.underline', reportPartHeaderProperties),
        textColor: get('font.color', reportPartHeaderProperties),
        alignment: get('alignment', reportPartHeaderProperties),
        wordWrap: get('wordWrap', reportPartHeaderProperties)
      });
    }
    return {};
  }
  const records = props.records;
  const { columns } = reportPart.getReportPartProperties();
  const columnAlignment = columns.alignment;
  const columnWordWrap = columns.wordWrap;
  let cellStyle = columnData.element.properties.getUIStyle();
  cellStyle.alignment = isNil(cellStyle.alignment) ? columnAlignment : cellStyle.alignment;
  cellStyle.wordWrap = isNil(cellStyle.wordWrap) ? columnWordWrap : cellStyle.wordWrap;

  const styleObject = stylingUtil.createStyleObject(cellStyle);
  const rawRecord = element.properties.hasRangePercent
    ? head(ProcessData.buildRawRecordFilterForPivotGrid(columnData, records))
    : {};

  const dataSource = {
    element: ReportPartUtils.buildDataSourceElement(reportPart, element),
    record: rawRecord
  };

  styleObject.color = ReportPartUtils.getConfigDataByRange(
    columnData.title,
    element.properties.dataFormattings.color.textColor,
    dataSource
  );

  return styleObject;
}

function isDisplayRowExpander(props, currentRowInfo, columnData) {
  const reportPart = props.reportPart;
  const reportPartContent = reportPart.reportPartContent;
  const rowExpanders = reportPartContent.haveRowExpander;

  return (
    rowExpanders &&
    currentRowInfo &&
    (((currentRowInfo.rowDataObject.size > 1 || props.forDrillDown) &&
      columnData.type === ColumnDataObject.COLUMN_TYPE.ROW &&
      currentRowInfo.firstRow) ||
      currentRowInfo.mustHaveExpander)
  );
}
/**
 * render exapand Icon for row
 * @param  {[type]} currentRowDataObject [Current rowDataObject]
 * @return {[type]}                      [description]
 */
function renderExpandIcon(props, currentRowDataObject) {
  const { reportPart, loader } = props;
  const reportPartContent = reportPart.reportPartContent;
  const rowExpanders = reportPartContent.haveRowExpander;

  if (!currentRowDataObject.isExpandable || !rowExpanders) {
    return null;
  }

  const rowExpandIcon = classnames(
    'custom-expand-collapse-icon',
    currentRowDataObject.isExpand ? 'icon-minus-circled' : 'icon-plus-circled'
  );
  const getSubData = function() {
    loader.startLoading();
    if (currentRowDataObject.isExpand) {
      if (!isEmpty(currentRowDataObject.data) || (props.forDrillDown && props.haveSuptotal)) {
        currentRowDataObject.toggleExpand();
        loader.endLoading();
      } else {
        props
          .getDrillDownRecords(reportPart, currentRowDataObject.buildDrillDownParams())
          .then(
            action(function(previewData) {
              const sideTotalColumns = props.sideTotalColumns;

              ProcessData.updateSideTotalMapping(
                sideTotalColumns,
                previewData.sideTotalMapping,
                reportPartContent.columns
              );
              // update data directly to current rowDataObject
              currentRowDataObject.data = map(mapValues(toString), previewData.records);
              currentRowDataObject.subTotalMapping = previewData.subTotalMapping;
              currentRowDataObject.toggleExpand();
            })
          )
          .finally(loader.endLoading.bind(loader));
      }
    } else {
      if (
        (!isEmpty(currentRowDataObject.childs) && get('isExpand', head(currentRowDataObject.childs))) ||
        (!isEmpty(get('data', head(currentRowDataObject.childs))) &&
          get('value', head(currentRowDataObject.childs)) !== 'undefined')
      ) {
        currentRowDataObject.toggleExpand();
        loader.endLoading();
      } else {
        const getChild = function(data, key) {
          for (let i = 0; i < data.length; i++) {
            if (data[i].key === key) {
              return data[i];
            }

            const found = getChild(data[i].childs, key);
            if (found) return found;
          }
        };
        props
          .getDrillDownRecords(reportPart, currentRowDataObject.buildDrillDownParams(true))
          .then(function(previewData) {
            const rowDataObjects = ProcessData.createRowDataObject(
              previewData.records,
              previewData,
              reportPartContent.rows,
              []
            );
            const newRow = getChild(rowDataObjects, currentRowDataObject.key);
            newRow && (currentRowDataObject.childs = newRow.childs);

            currentRowDataObject.toggleExpand();
          })
          .finally(loader.endLoading.bind(loader));
      }
    }
  };

  return <span className={rowExpandIcon} onClick={getSubData} />;
}

/**
 * add 'All' row at the first row for drillDown only
 * @param  {Object} newData
 * @return {Array}
 */
function getFirstRecordForDrilldown(props, newData) {
  const allRow = props.allRow;
  const allRowObject = get('currentRow.rowDataObject', allRow);

  if (allRowObject.isExpand) {
    allRow.currentRow.isSupTotalRow = false;
    return concat([allRow], newData);
  } else {
    allRow.currentRow.isSupTotalRow = props.haveSupTotal;
    return [allRow];
  }
}

const getColumnGroup = get('element.properties.headerFormating.columnGroup');

function processColumnGroup(columnList, level) {
  const setHeaderValue = function(itemHeader, leaf) {
    const columnHeader = getColumnGroup(leaf);

    itemHeader.value = columnHeader;
    itemHeader.isLeaf = false;
    itemHeader.isHidden = true;
    itemHeader.level = level;
    itemHeader.isInvisible = false;
    itemHeader.isColumnGroup = true;
    itemHeader.hideTopLeftBorder = !columnHeader;
    itemHeader.childs = [leaf];
  };

  const createNewGroup = function(leaf) {
    const header = new ColumnDataObject.ColumnDataObject();

    setHeaderValue(header, leaf);
    return header;
  };

  const addNewGroup = function(acc, leaf) {
    const newGroup = createNewGroup(leaf);

    return {
      leafRowHeaders: concat(acc.leafRowHeaders, newGroup),
      lastGroup: newGroup,
      lastLeaf: leaf
    };
  };

  return flow(
    reduce(
      function(acc, leaf) {
        if (acc.lastLeaf && getColumnGroup(acc.lastLeaf) === getColumnGroup(leaf)) {
          acc.lastGroup.childs = concat(acc.lastGroup.childs, leaf);
          return set('lastLeaf', leaf, acc);
        }
        return addNewGroup(acc, leaf);
      },
      {
        leafRowHeaders: [],
        lastGroup: null,
        lastLeaf: null
      }
    ),
    get('leafRowHeaders')
  )(columnList);
}

function buildPageColumnHeader(props) {
  const { forDrillDown, rowHeaderColumnDataObject, columnDataObjects, reportPart, embededReportPart } = props;
  let sideTotalColumns = props.sideTotalColumns;
  const reportPartContent = reportPart.reportPartContent;
  const reportPartProperties = reportPartContent.properties;
  const pivotColumnsPerExportedPage = reportPartProperties.view.pivotColumnsPerExportedPage;
  const haveGroupForRow = some(getColumnGroup, rowHeaderColumnDataObject);
  const maxLevel = reportPartContent.columns.visibleElement.length;
  const isMobile = BrowserUtils.isMobileDevice();

  let fullRowHeader = rowHeaderColumnDataObject;
  let onlyColumnGroupRowHeader = fullRowHeader;
  let emptyRowHeader = [ProcessData.createBlankHeaders(0, 1, rowHeaderColumnDataObject)];

  if (haveGroupForRow) {
    onlyColumnGroupRowHeader = fullRowHeader = processColumnGroup(fullRowHeader, maxLevel);
    maxLevel > 1 && (fullRowHeader = [ProcessData.createBlankHeaders(0, maxLevel - 1, fullRowHeader)]);
  } else if (maxLevel) {
    fullRowHeader = [ProcessData.createBlankHeaders(0, maxLevel, fullRowHeader)];
  }

  const buildPage = curry(function(pageColumn) {
    const haveGroupForColumn = forDrillDown && some(getColumnGroup, pageColumn);
    const returnRow = haveGroupForColumn && !haveGroupForRow ? emptyRowHeader : fullRowHeader;
    let returnColumn = pageColumn;

    if (haveGroupForColumn) {
      returnColumn = processColumnGroup(pageColumn, 1);
    } else if (forDrillDown && haveGroupForRow) {
      returnColumn = [ProcessData.createBlankHeaders(0, 1, pageColumn)];
    }

    return concat(returnRow, returnColumn);
  });

  let result = {
    pageColumnHeader: [concat(buildPage(columnDataObjects), sideTotalColumns)],
    pageStatus: true
  };
  let sideTotalPage = [];

  if (props.printingMode && pivotColumnsPerExportedPage) {
    if (forDrillDown) {
      result = {
        pageColumnHeader: flow(
          chunk(pivotColumnsPerExportedPage),
          map(buildPage)
        )(columnDataObjects),
        pageStatus: true
      };
    } else if (every({ pivotColumn: pivotColumnsPerExportedPage }, columnDataObjects)) {
      if (!isEmpty(sideTotalColumns)) {
        if (haveGroupForRow) {
          sideTotalColumns = [ProcessData.createBlankHeaders(0, 1, sideTotalColumns)];
        }
        sideTotalPage = [concat(onlyColumnGroupRowHeader, sideTotalColumns)];
      }
      result = {
        pageColumnHeader: flow(
          chunk(1),
          map(buildPage),
          concat(placeholder, sideTotalPage)
        )(columnDataObjects),
        pageStatus: true
      };
    } else {
      result = {
        haveGroupForRow: haveGroupForRow,
        fullRowHeader: fullRowHeader,
        onlyColumnGroupRowHeader: onlyColumnGroupRowHeader,
        pageStatus: false
      };
    }
  }

  result.getStickyValue = function(value) {
    return embededReportPart || isMobile ? false : value;
  };

  return result;
}

/**
 * handling all states of Pivot grid
 * @param  {Object} nextProps
 */
function handleStates(nextProps) {
  const addSideTotal = !!getOr(false, 'reportPartContent.columns.properties.addSideTotal', nextProps.reportPart);
  const {
    reportPartContent,
    previewData,
    previewData: { subTotalMapping }
  } = nextProps.reportPart;
  const reportPartProperties = reportPartContent.properties;
  const pivotColumnsPerExportedPage = reportPartProperties.view.pivotColumnsPerExportedPage;
  let collapseDrilldownByDefault;

  if (nextProps.printingMode) {
    collapseDrilldownByDefault = false;
  } else if (reportPartContent.collapseStatus === COLLAPSE_STATUS.EXPAND) {
    collapseDrilldownByDefault = false;
  } else if (reportPartContent.collapseStatus === COLLAPSE_STATUS.COLLAPSE) {
    collapseDrilldownByDefault = true;
  } else {
    collapseDrilldownByDefault = reportPartProperties.view.collapseDrilldownByDefault;
  }

  const rowHeaderColumnDataObject = ProcessData.createRowHeadersDataObject(reportPartContent.rows);
  const columnDataObjects = ProcessData.createColumnDataObject(
    nextProps.records,
    previewData,
    reportPartContent.columns,
    reportPartContent.values,
    reportPartContent.separators,
    nextProps.keyPath
  );
  let sideTotalColumns = [];
  const rowDataObject = ProcessData.createRowDataObject(
    nextProps.records,
    previewData,
    reportPartContent.rows,
    nextProps.keyPath || [],
    null,
    !collapseDrilldownByDefault
  );
  let supTotalDataRow = null;
  let allRow = null;
  let haveSeparator = !isEmpty(nextProps.keyPath);
  const haveSupTotal = some(item => {
    const field = find(['fieldNameAlias', item.fieldNameAlias], reportPartContent.fieldCollection);
    return field && field.isVisible;
  }, subTotalMapping);
  const forDrillDown = reportPartContent.gridStyle === constants.GRID_STYLES.DRILL_DOWN.value;
  const isLastSeparatorVisible = flow(
    sortBy('position'),
    last,
    get('isVisible')
  )(reportPartContent.separators.elements);
  const allGroupSuptotal = haveSupTotal && (!forDrillDown || (haveSeparator && isLastSeparatorVisible));

  if (addSideTotal) {
    sideTotalColumns = ProcessData.createSideTotal(previewData, reportPartContent.values);
    ProcessData.updateSideTotalMapping(sideTotalColumns, previewData.sideTotalMapping, reportPartContent.columns);
  }

  const firstRowElement = flow(
    sortBy('position'),
    head
  )(reportPartContent.rows.visibleElement);

  if (allGroupSuptotal) {
    const lastSeparatorElement = flow(
      sortBy('position'),
      last
    )(reportPartContent.separators.visibleElement);
    const lastSeparatorsValue = flow(
      find(function(item) {
        return item.element === lastSeparatorElement;
      }),
      getOr('', 'value')
    )(nextProps.keyPath);

    supTotalDataRow = ProcessData.createSupTotalRowDataObject(
      firstRowElement.field.fieldNameAlias,
      rowDataObject,
      lastSeparatorElement,
      lastSeparatorsValue,
      previewData
    );
  }

  if (forDrillDown) {
    allRow = ProcessData.createAllRowDataObject(
      firstRowElement.field.fieldNameAlias,
      rowDataObject,
      nextProps.keyPath,
      reportPartContent.rows.elements.length + 1
    );
  }

  if (!reportPartContent.useSeparator) {
    reportPartContent.setCollapseStatus(COLLAPSE_STATUS.DEFAULT);
  }

  return {
    rowDataObject,
    rowHeaderColumnDataObject,
    columnDataObjects,
    sideTotalColumns,
    subTotalMapping,
    forDrillDown,
    haveSeparator,
    haveSupTotal,
    supTotalDataRow,
    allGroupSuptotal,
    allRow,
    pivotColumnsPerExportedPage
  };
}

function toggleAllLevels(props, value) {
  const recursiveToggle = action(rows => {
    !isEmpty(rows) &&
      rows.forEach(row => {
        if (!isEmpty(row.childs)) {
          recursiveToggle(row.childs);
          row.isExpand !== value && row.toggleExpand(value);
        }
      });
  });
  recursiveToggle(props.rowDataObject);
}

function collapseAll(props, value) {
  const {
    reportPart,
    loader,
    reportPart: { reportPartContent }
  } = props;
  // Always expand node All
  props.allRow.currentRow.rowDataObject.isExpand = true;

  if (props.haveSupTotal) {
    toggleAllLevels(props, false);
  } else {
    loader.startLoading();
    if (!some(row => isEmpty(row.data), props.rowDataObject)) {
      toggleAllLevels(props, false);
      loader.endLoading();
    } else {
      reportPartContent.setCollapseStatus(-1);
      var minLevel = 0;
      let firstVisibleLevel = Math.min(...reportPartContent.rows.visibleElement.map(e => e.position)) - 1;
      minLevel = Math.max(minLevel, firstVisibleLevel);
      props
        .getDrillDownRecords(reportPart, { expandedLevel: minLevel, filters: [] }, reportPart.previewData.paging.pageIndex)
        .then(previewData => reportPart.setQueryData(previewData))
        .finally(loader.endLoading.bind(loader));
    }
  }
}

function expandAll(props, value) {
  const {
    reportPart,
    loader,
    reportPart: { reportPartContent }
  } = props;
  // Always expand node All
  props.allRow.currentRow.rowDataObject.isExpand = true;

  if (props.haveSupTotal) {
    toggleAllLevels(props, true);
  } else {
    loader.startLoading();
    reportPartContent.setCollapseStatus(1);
    props
      .getDrillDownRecords(reportPart, { expandedLevel: -1, filters: [] }, reportPart.previewData.paging.pageIndex)
      .then(previewData => reportPart.setQueryData(previewData))
      .finally(loader.endLoading.bind(loader));
  }
}

export default compose(
  withPropsOnChange(['records'], handleStates),
  withHandlers({
    renderExpandIcon: curry(renderExpandIcon),
    getFirstRecordForDrilldown: curry(getFirstRecordForDrilldown),
    isDisplayRowExpander: curry(isDisplayRowExpander),
    fnGetHeaderCellContentStyle: curry(fnGetHeaderCellContentStyle),
    fnGetHeaderCellContentForPivotHeader: curry(fnGetHeaderCellContentForPivotHeader),
    fnGetHeaderCellStyle: curry(fnGetHeaderCellStyle),
    getCellStyle: curry(getCellStyle),
    getCellContentStyle: curry(getCellContentStyle),
    collapseAll: curry(collapseAll),
    expandAll: curry(expandAll),
    getCellData: curry(getCellData)
  }),
  observer,
  withProps(buildPageColumnHeader)
);



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartGrid/Izenda.ReportPartGridPivotActions.js